import { AccessForbiddenException, NotAuthorizedException, NotFoundException } from "@utils/type/exception";
import type { AxiosError } from "axios";
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { handleLockerResponseHeaders } from "@utils/type/locker";
import { getCsrfToken } from "@utils/csrfUtils";
import { stringifyParams } from "@utils/vue-migration/common/urlUtils";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";
import { type ApiResult, type GaApiCallProps, gaApiCallWithoutAuth } from "@utils/httpClientCore";

type ResponseHandler<T = unknown> = {
  handle: (response: AxiosResponse<T>) => void;
};

class LockerResponseHandler implements ResponseHandler {
  handle(response: AxiosResponse): void {
    handleLockerResponseHeaders((name) => (name in response.headers ? response.headers[name] : undefined));
  }
}

let axiosInstance: AxiosInstance | null = null;
/** Creating the instance for axios */
export const getHttpClient = (): AxiosInstance => {
  if (axiosInstance !== null) {
    return axiosInstance;
  }
  /** Default config for axios instance */
  const config: AxiosRequestConfig = {
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
    paramsSerializer: function (params) {
      return stringifyParams(params);
    },
  };
  const httpClient = axios.create(config);
  httpClient.interceptors.request.use(async function (config) {
    if (
      config.method !== undefined &&
      ["POST", "PUT", "DELETE", "PATCH"].includes(config.method.toUpperCase())
      /*  && // Only for relevant HTTP verbs
      new URL(axios.getUri(config), window.location.href).origin === window.location.origin // and only for same-origin requests (the token must no be leaked)*/
    ) {
      config.headers.set("X-XSRF-Token", await getCsrfToken());
    }
    return config;
  });

  httpClient.interceptors.response.use(
    (response: AxiosResponse) => {
      new LockerResponseHandler().handle(response);
      return response;
    },
    (error: AxiosError) => {
      if (error.response != null && error.response.status === 401) {
        throw new NotAuthorizedException(error.config?.method === "get");
      } else if (error.response != null && error.response.status === 403) {
        throw new AccessForbiddenException(error);
      } else if (error.response != null && error.response.status === 404) {
        throw new NotFoundException();
      }
      return Promise.reject(error);
    },
  );
  axiosInstance = httpClient;
  return httpClient;
};

export async function gaApiCall(props: GaApiCallProps): Promise<ApiResult<string>> {
  const csrfToken = await getCsrfToken();
  return gaApiCallWithoutAuth({ apiCallProps: props, authHeaders: { "X-XSRF-TOKEN": csrfToken } });
}

export function getErrorMessage(opts: { errorCode: string | null; errorMessage: string | null }, t: TranslationFn): string {
  const errorMessage = [opts.errorMessage ?? t("general:error.unexpected", "Error")];
  if (opts.errorCode !== null) {
    errorMessage.push(t("general:error.code", [opts.errorCode]));
  }

  return errorMessage.join(" ");
}
